import type { LiteralUnion } from "type-fest";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { type AudioType, getMostRecentTask, isVoiceTask, type Locale, sdkEventBus } from "@/sdk";
import { ENGLISH, getLanguageState } from "./getLanguageState";

export type LanguageStoreState = {
    label: LanguageLabel;
    language: Language;
    audioType: AudioType;
    locale: Locale;
    abbreviation: LanguageAbbreviation;
};

export type LanguageStoreActions = {
    updateLanguage: (locale?: LanguageIdentifiers | null) => void;
};

export type LanguageStoreProps = LanguageStoreState & LanguageStoreActions;

export type Language = "en-US" | "es-US";
export type LanguageLabel = "English" | "Spanish";

export const abbreviations = ["Eng", "Spa"] as const;
export type LanguageAbbreviation = (typeof abbreviations)[number];

export type LanguageIdentifiers = LiteralUnion<
    Locale | LanguageAbbreviation | AudioType | Language | LanguageLabel,
    string
>;

export const useLanguageStore = create<LanguageStoreProps>()(
    persist(
        devtools(
            (set) => ({
                ...ENGLISH, // default to english

                updateLanguage(lang?: LanguageIdentifiers | null) {
                    set(getLanguageState(lang), false, "updateLanguage");
                },
            }),
            {
                enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                store: "language-store",
                name: "workspace/sales-tools",
            },
        ),
        {
            name: "language-store",
        },
    ),
);

/**
 * Defaults to locale on incoming call
 * Saves selected option to local storage
 * resets local storage between calls
 */
sdkEventBus.on("session_changed", ({ action, session }) => {
    switch (action) {
        case "start": {
            const task = getMostRecentTask(session);

            if (isVoiceTask(task)) {
                useLanguageStore.getState().updateLanguage(task.locale);
            }
            break;
        }
        case "end": {
            useLanguageStore.persist.clearStorage();
            break;
        }
    }
});
